import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isArray } from 'lodash';

import { useToast } from 'Store/toast';
import { useLoader } from 'Store/loader';
import { useModal } from 'Store/modal';
import { useUserInfo } from 'Store/userInfo';
import deleteRoleAccess from 'Api/role/deleteRoleAccess';

import { useRoleState } from 'View/role/state';

const useDeleteRoleAccess = () => {
  const { t } = useTranslation('role');
  const queryClient = useQueryClient();
  const { onOpenToast } = useToast();
  const { onShowLoader, onResetLoader } = useLoader();
  const { onResetModal } = useModal();
  const { updateState, filterState } = useRoleState();
  const { organization } = useUserInfo();

  return useMutation({
    mutationFn: async (id) => {
      onShowLoader({ variant: 'loader-primary' });
      return await deleteRoleAccess({ id });
    },
    onSuccess: () => {
      onOpenToast({
        status: 'success',
        description: t('deleteSuccess'),
        duration: 5,
        alwaysShow: false,
      });

      updateState({ alertProps: {} });

      queryClient.invalidateQueries({ queryKey: ['role', filterState] });
      queryClient.invalidateQueries({ queryKey: ['all-roles', organization?.id] });
    },
    onError: (error) => {
      const errorMessage = isArray(error?.response?.data?.errors) ? error?.response?.data?.errors[0]?.message : t('deleteErrorDescription');

      updateState({
        alertProps: {
          isOpen: true,
          type: 'error',
          title: t('deleteErrorTitle'),
          description: errorMessage,
          onClose: () => updateState({ alertProps: {} }),
          marginBottom: '12px',
        },
      });
    },
    onSettled: () => {
      onResetLoader();
      onResetModal();
    },
  });
};

export default useDeleteRoleAccess;